import { createFeatureSelector, createSelector } from '@ngrx/store';

import { GuaranteeApplicationStatus } from '../models/guarantee-application.interface';
import { PrescriptionTypes } from '../models/prescription.interface';
import { GuaranteeStatusFilters, PrescriptionTypeFilters, PrescriptionsFeatureState } from './prescriptions.state';

export const selectPrescriptionsFeature = createFeatureSelector<PrescriptionsFeatureState>('prescriptions');

// Prescriptions state
const selectPrescriptionsState = createSelector(selectPrescriptionsFeature, state => state.prescriptions);

export const selectPrescriptionSaved = createSelector(selectPrescriptionsState, state => state.prescriptionSaved);
export const selectPrescriptionEdited = createSelector(selectPrescriptionsState, state => state.prescriptionEdited);
export const selectPrescriptionDeleted = createSelector(selectPrescriptionsState, state => state.prescriptionDeleted);
export const selectPrescriptionsInitRequired = createSelector(selectPrescriptionsState, state => state.initRequired);
export const selectPrescriptionsFilters = createSelector(selectPrescriptionsState, state => state.guaranteesFilters);
export const selectPrescriptionsPage = createSelector(selectPrescriptionsState, state => state.page);
export const selectPrescriptionsPageSize = createSelector(selectPrescriptionsState, state => state.pageSize);
export const selectPrescriptionsCount = createSelector(selectPrescriptionsState, state => state.count);
export const selectPrescriptionsHasNextPage = createSelector(selectPrescriptionsState, state => state.hasNextPage);
export const selectPrescriptionsHasPreviousPage = createSelector(selectPrescriptionsState, state => state.hasPreviousPage);
export const selectPrescriptionsGuarantees = createSelector(selectPrescriptionsState, state => state.guarantees);

// Memoized computed selectors for better performance
export const selectFilteredGuarantees = createSelector(selectPrescriptionsGuarantees, selectPrescriptionsFilters, (guarantees, filters) => {
  if (!guarantees?.length) return guarantees;

  return guarantees.filter(guarantee => {
    // Search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      const matchesSearch = guarantee.patientName?.toLowerCase().includes(searchTerm) || guarantee.status?.toLowerCase().includes(searchTerm);
      if (!matchesSearch) return false;
    }

    // Prescription type filter
    if (filters.prescriptionType && filters.prescriptionType !== PrescriptionTypeFilters.All) {
      const hasMatchingType = guarantee.prescriptions?.some(p => {
        switch (filters.prescriptionType) {
          case PrescriptionTypeFilters.Drugs:
            return p.type === PrescriptionTypes.Drugs;
          case PrescriptionTypeFilters.RelatedSpecialists:
            return p.type === PrescriptionTypes.RelatedSpecialists;
          case PrescriptionTypeFilters.InstrumentalDiagnostic:
            return p.type === PrescriptionTypes.InstrumentalDiagnostic;
          case PrescriptionTypeFilters.LaboratoryService:
            return p.type === PrescriptionTypes.LaboratoryService;
          default:
            return true;
        }
      });
      if (!hasMatchingType) return false;
    }

    // Guarantee status filter
    if (filters.guaranteeStatus && filters.guaranteeStatus !== GuaranteeStatusFilters.All) {
      switch (filters.guaranteeStatus) {
        case GuaranteeStatusFilters.WaitProcessing:
          if (guarantee.status !== GuaranteeApplicationStatus.WaitProcessing) return false;
          break;
        case GuaranteeStatusFilters.Guaranteed: {
          // Check if any prescription has guaranteed status
          const hasGuaranteed = guarantee.prescriptions?.some(p => p.guaranteeStatus === 'Guaranteed');
          if (!hasGuaranteed) return false;
          break;
        }
        case GuaranteeStatusFilters.NotGuaranteed: {
          // Check if any prescription has not guaranteed status
          const hasNotGuaranteed = guarantee.prescriptions?.some(p => p.guaranteeStatus === 'NotGuaranteed');
          if (!hasNotGuaranteed) return false;
          break;
        }
      }
    }

    // History status filter (Active vs Archived)
    if (filters.historyStatus) {
      const hasActiveStatus = guarantee.prescriptions?.some(p => p.status !== 'Archived');
      if (filters.historyStatus === 'Active' && !hasActiveStatus) return false;
      if (filters.historyStatus === 'Archived' && hasActiveStatus) return false;
    }

    return true;
  });
});

export const selectGuaranteesWithPrescriptionCounts = createSelector(selectFilteredGuarantees, guarantees =>
  guarantees.map(guarantee => ({
    ...guarantee,
    activePrescriptionsCount: guarantee.prescriptions?.filter(p => p.status !== 'Archived').length || 0,
    totalPrescriptionsCount: guarantee.prescriptions?.length || 0,
  }))
);

export const selectPaginatedGuarantees = createSelector(
  selectGuaranteesWithPrescriptionCounts,
  selectPrescriptionsPage,
  selectPrescriptionsPageSize,
  (guarantees, page, pageSize) => {
    const startIndex = page * pageSize;
    const endIndex = startIndex + pageSize;
    return guarantees.slice(startIndex, endIndex);
  }
);
export const selectDraftsProcessing = createSelector(selectPrescriptionsState, state => state.draftsProcessing);
export const selectPrescriptionsLoading = createSelector(selectPrescriptionsState, state => state.loading);
export const selectPrescriptionsGuaranteesFetching = createSelector(selectPrescriptionsState, state => state.guaranteesFetching);
export const selectTargetPrescription = createSelector(selectPrescriptionsState, state => state.targetPrescription);
export const selectPrescriptionsIsPolling = createSelector(selectPrescriptionsState, state => state.isPolling);

// Guarantee flow state
export const selectGuarantee = createSelector(selectPrescriptionsState, state => state.guarantee);
export const selectGuaranteePrescriptions = createSelector(selectGuarantee, guarantee => guarantee.prescriptions);
export const selectGuaranteeFreeForm = createSelector(selectGuarantee, guarantee => guarantee.freeForm);

// Drafts state
const selectDraftsState = createSelector(selectPrescriptionsFeature, state => state.drafts);

export const selectPrescriptionDrafts = createSelector(selectDraftsState, state => state.drafts);
export const selectRelatedSpecialistDraft = createSelector(selectPrescriptionDrafts, drafts => drafts.find(d => d.type === PrescriptionTypes.RelatedSpecialists) ?? null);

export const selectDraftsByType = createSelector(selectPrescriptionDrafts, drafts => {
  const draftsByType = new Map();
  drafts.forEach(draft => {
    const type = draft.type;
    if (!draftsByType.has(type)) {
      draftsByType.set(type, []);
    }
    draftsByType.get(type).push(draft);
  });
  return draftsByType;
});

export const selectDraftsCount = createSelector(selectPrescriptionDrafts, drafts => drafts.length);

export const selectHasPendingDrafts = createSelector(selectDraftsCount, count => count > 0);

export const convertDraftsToDisplayItems = createSelector(selectPrescriptionDrafts, drafts =>
  drafts.map(draft => ({
    ...draft,
    isDraft: true,
  }))
);
